import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { createStore, useStore } from '@w/react-store'
import { useMediaQuery } from '@w/utils-fe'
import { Popover, PopoverPopup, PopoverTitle } from '@w/ui/components/popover'
import { IconButton } from '@w/ui/components/icon-button'
import { FullscreenIcon, ExitFullscreenIcon, XIcon } from '@w/ui/components/icon'

function createPanelState() {
  const state = createStore<{ active: string | null; anchor: HTMLElement | null }>({ active: null, anchor: null })
  return {
    state,
    open(this: void, id: string, anchor?: HTMLElement | null) { state.update(current => ({ active: id, anchor: anchor ?? (current.active === id ? current.anchor : null) })) },
    close(this: void, id: string) { state.update(current => current.active === id ? { active: null, anchor: null } : current) },
  }
}
const MobileReviewContext = createContext<ReturnType<typeof createPanelState> | null>(null)
const MobileReviewContainer = createContext<HTMLElement | null>(null)

export function MobileReviewProvider({ container, children }: { container?: HTMLElement | null; children: ReactNode }) {
  const [controller] = useState(createPanelState)
  return <MobileReviewContext value={controller}><MobileReviewContainer value={container ?? null}>{children}</MobileReviewContainer></MobileReviewContext>
}

export function useMobileReview() {
  const controller = useContext(MobileReviewContext)
  const container = useContext(MobileReviewContainer)
  const compact = useMediaQuery('(max-width: 599px)')
  if (!controller) throw new Error('Mobile review requires its editor provider')
  const { active, anchor } = useStore(controller.state)
  return { enabled: compact && !!container, active, anchor, container, open: controller.open, close: controller.close }
}

export function MobileReviewPanel({ id, title, onClose, children, composer = false }: { id: string; title: string; onClose: () => void; children: ReactNode; composer?: boolean }) {
  const mobile = useMobileReview()
  const panelRef = useRef<HTMLElement>(null)
  const { enabled, active, anchor, close } = mobile
  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!composer || !enabled || active !== id || !anchor || !panel) return () => {}
    let frame = 0
    const reveal = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        let scroller = anchor.parentElement
        while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement
        if (!scroller) return
        const top = anchor.getBoundingClientRect().top
        const visibleBottom = panel.getBoundingClientRect().top - 24
        const visibleTop = scroller.getBoundingClientRect().top + 16
        if (top + 24 > visibleBottom) scroller.scrollTop += top + 24 - visibleBottom
        else if (top < visibleTop) scroller.scrollTop -= visibleTop - top
      })
    }
    const observer = new ResizeObserver(reveal)
    observer.observe(panel)
    window.visualViewport?.addEventListener('resize', reveal)
    reveal()
    return () => { observer.disconnect(); cancelAnimationFrame(frame); window.visualViewport?.removeEventListener('resize', reveal) }
  }, [composer, enabled, active, id, anchor])
  useEffect(() => {
    if (!enabled || active !== id) return () => {}
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) { close(id); onClose() }
    }
    document.addEventListener('keydown', dismiss)
    return () => { document.removeEventListener('keydown', dismiss) }
  }, [enabled, active, close, id, onClose])
  if (!mobile.enabled || mobile.active !== id || !mobile.container) return null
  if (!composer) return <MobileThreadPopover key={id} title={title} anchor={mobile.container} onClose={() => { mobile.close(id); onClose() }}>{children}</MobileThreadPopover>
  return createPortal(<section ref={panelRef} tabIndex={-1} aria-label={title} data-testid="mobile-review-panel" data-review-mobile-panel="" data-review-mobile-composer={composer || undefined} className="bg-surface-3 w-full rounded-none p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
    <div className="max-h-[min(40dvh,var(--review-panel-height,40dvh))] overflow-y-auto overscroll-contain">{children}</div>
  </section>, mobile.container)
}

function MobileThreadPopover({ title, anchor, onClose, children }: { title: string; anchor: HTMLElement; onClose: () => void; children: ReactNode }) {
  const [expanded, setExpanded] = useState(false)
  return <Popover open modal={false} onOpenChange={open => { if (!open) onClose() }}>
    <PopoverPopup ref={positionThreadPopover} anchor={anchor} side="top" align="center" sideOffset={8} showArrow={false}
      data-testid="mobile-review-panel" data-review-mobile-panel="" data-expanded={expanded}
      initialFocus={false} finalFocus={false}
      positionerClassName={expanded ? 'fixed! inset-0! h-dvh! w-screen! max-w-none! transform-none! [--available-height:100dvh]!' : undefined}
      className={expanded ? 'h-full! w-full! rounded-none [&>[data-slot=popover-viewport]]:flex [&>[data-slot=popover-viewport]]:flex-col' : 'w-[calc(100vw-1rem)] max-w-sm'}>
      <header className="mb-2 flex items-center gap-2">
        <PopoverTitle className="mr-auto">{title}</PopoverTitle>
        <IconButton size="sm" onClick={() => { setExpanded(value => !value) }}>{expanded ? <ExitFullscreenIcon /> : <FullscreenIcon />}{expanded ? 'Collapse comment' : 'Expand comment'}</IconButton>
        <IconButton size="sm" onClick={onClose}><XIcon />Close comment</IconButton>
      </header>
      <div className={expanded ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain' : 'max-h-[min(60dvh,var(--available-height))] overflow-y-auto overscroll-contain'}>{children}</div>
    </PopoverPopup>
  </Popover>
}

/** Keep portalled replies above the software keyboard. */
function positionThreadPopover(popup: HTMLDivElement | null) {
  const view = popup?.ownerDocument.defaultView
  if (!popup || !view) return () => {}
  const viewport = view.visualViewport
  const update = () => {
    popup.style.translate = ''
    const top = viewport?.offsetTop ?? 0
    const bottom = top + (viewport?.height ?? view.innerHeight)
    const bounds = popup.getBoundingClientRect()
    const shift = Math.max(top - bounds.top, Math.min(0, bottom - bounds.bottom))
    popup.style.translate = `0 ${shift}px`
  }
  viewport?.addEventListener('resize', update)
  viewport?.addEventListener('scroll', update)
  return () => {
    viewport?.removeEventListener('resize', update)
    viewport?.removeEventListener('scroll', update)
  }
}
